import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  FileText,
  Trash2,
  Upload,
  X,
  Image as ImageIcon,
} from "lucide-react";
import images from "../../assets/images";
import { useS3Upload, S3TempUrl } from "../../utils/s3Client";
import { useDropzone } from "react-dropzone";
import {
  useAsset,
  useEquipmentStore,
  usePremisesStore,
  useRegionStore,
} from "../../store/store";
import {
  AssetsState,
  initialChecks,
  maintenance,
} from "../../types/api";
import { DotLoader } from "../../components/loader/Loader";
import { useAuth } from "../../auth/AuthProvider";
import { generateMaintenanceExcel } from "./GenerateExcel";
import { v4 as uuidv4 } from "uuid";
import DynamicAssetFields from "../../components/AssetInspection/DynamicAssetFields";

interface CreateAssetProps {
  mode: "create" | "edit" | "view";
  setIsCreating: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIds: string[];
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
  isCreating: boolean;
}

const FREQUENCY_OPTIONS = ["Daily", "Weekly", "Monthly", "Quarterly", "Yearly"];
const INVENTORY_OPTIONS = [
  "Fire Safety",
  "Health & Safety",
  "First Aid",
  "Food Safety",
];

const inputBase =
  "w-full px-3 py-2 border border-[#a5b7cf] rounded-lg text-sm text-[#5a687c] outline-none transition-all focus:border-[#00A8A5] focus:ring-1 focus:ring-[#00A8A5]/20 bg-white";
const inputDisabled =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 bg-gray-50 cursor-not-allowed outline-none";

const fieldClass = (disabled?: boolean) => (disabled ? inputDisabled : inputBase);

const AddAssets: React.FC<CreateAssetProps> = ({
  mode,
  setIsCreating,
  selectedIds,
  setSelectedIds,
}) => {
  const isView = mode === "view";
  const isEdit = mode === "edit";
  const isCreate = mode === "create";

  const [selectEquipment, setSelectEquipment] = useState("");
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { roleData } = useAuth();
  const { equipmentData } = useEquipmentStore();
  const [maintenanceHandled, setMaintenanceHandled] = useState(false);

  const [maintenanceState, setMaintenanceState] = useState<maintenance>({
    internalExternal: "",
    Checked: "",
    lastDateOfCheck: "",
    checkCompletedBy: "",
    observation: "",
    actionTaken: "",
    recheckDate: "",
    recheckBy: "",
    files: [],
    image: "",
    equipment: selectedIds || [],
  });

  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedPremise, setSelectedPremise] = useState("");
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [selectedEquipmentManufacture, setSelectedEquipmentManufacture] =
    useState("");
  const [selectedEquipmentExpire, setSelectedEquipmentExpire] = useState("");

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setMaxDate(today);
    setMinDate(today);
  }, []);

  const { getsignedurl } = S3TempUrl();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();
  if (!regions.length) fetchRegions();

  const {
    allAssets,
    equipmentInventory,
    currentAssets,
    currentMaintenance,
    allMaintenance,
    createAssetsHandler,
    updateAssetsHandler,
    createMaintenanceHandler,
    getAllAssetsHandler,
    clearData,
  } = useAsset();
  const { allAssetsEquipment, allAssetSubcategories, assetSubcategoryData } =
    useEquipmentStore();

  const [assets, setAssets] = useState<AssetsState>({
    premisesName: "",
    premisesId: "",
    equipmentInventory: "",
    equipmentInventoryName: "",
    equipmentSubCategory: "",
    region: "",
    name: "",
    image: "",
    vendorName: "",
    mobileNumber: "",
    vendorEmail: "",
    inventory: "",
    unitNumber: "",
    manufacture: "",
    type: "",
    installedOn: "",
    totalNumber: "",
    uploadReferenceDocument: "",
    equipmentManufactureDate: "",
    equipmentExpireDate: "",
    floor: "",
    areaName: "",
    checksAssets: [],
    files: [],
    panelNo: "",
    loopNo: "",
    deviceNo: "",
    loopType: "",
  });

  const [currentCheck, setCurrentCheck] = useState<initialChecks>({
    _id: "",
    checkType: "",
    frequency: "",
    nextScheduled: "",
    createdAt: "",
  });

  useEffect(() => {
    allAssetSubcategories();
    getAllAssetsHandler();
  }, [allAssetSubcategories, getAllAssetsHandler]);

  const calculateNextScheduledDate = (frequency: string) => {
    const today = new Date();
    let nextDate = new Date(today);
    switch (frequency) {
      case "Daily":
        nextDate.setDate(today.getDate() + 1);
        break;
      case "Weekly":
        nextDate.setDate(today.getDate() + 7);
        break;
      case "Monthly":
        nextDate.setMonth(today.getMonth() + 1);
        break;
      case "Quarterly":
        nextDate.setMonth(today.getMonth() + 3);
        break;
      default:
        nextDate = today;
    }
    return nextDate.toISOString().split("T")[0];
  };

  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();

  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (assets.image) {
        try {
          const key = assets.image.split("amazonaws.com/")[1];
          const url = await getsignedurl(key, "image/jpeg");
          setImageUrl(url);
        } catch (err) {
          console.error("Failed to get signed URL:", err);
          setImageUrl(null);
        }
      } else {
        setImageUrl(null);
      }
    };
    fetchSignedUrl();
  }, [assets.image, getsignedurl]);

  const validateMobileNumber = (number: string) => /^\d{10}$/.test(number);

  const handleAssetsInput = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    if (name === "mobileNumber") {
      setError(
        !validateMobileNumber(value) ? "Mobile number must be 10 digits." : null
      );
    }
    setAssets((prev) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
      equipmentInventory: prev.equipmentInventory || equipmentInventory || "",
    }));
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (isView) return;
    try {
      const uploadedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          const url = await uploadFileToS3(file);
          return { name: file.name, url, type: file.type, size: file.size };
        })
      );
      setAssets({
        ...assets,
        files: [
          ...(Array.isArray(assets.files) ? assets.files : []),
          ...uploadedFiles,
        ],
      });
    } catch (err) {
      setError("Failed to upload files");
      console.error("File upload error:", err);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg"],
      "image/png": [".png"],
    },
    maxSize: 5 * 1024 * 1024,
    disabled: isView,
  });

  const handleViewFile = async (file: { name: string; type: string }) => {
    try {
      const fileurl = await getsignedurl(file.name, file.type);
      window.open(fileurl, "_blank");
    } catch (err) {
      setError("Failed to view file");
      console.error("Error viewing file:", err);
    }
  };

  const handleRemoveFile = (index: number) => {
    if (isView) return;
    const newFiles = Array.isArray(assets.files) ? [...assets.files] : [];
    newFiles.splice(index, 1);
    setAssets({ ...assets, files: newFiles });
  };

  const handleCheckInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setCurrentCheck((prev) => ({
      ...prev,
      [name]: value,
      nextScheduled:
        name === "frequency"
          ? calculateNextScheduledDate(value)
          : prev.nextScheduled,
    }));
  };

  const validateCheck = (check: initialChecks) => {
    if (!check.checkType || !check.frequency || !check.nextScheduled) {
      setError("All check fields are required.");
      return false;
    }
    setError(null);
    return true;
  };

  const handleAddCheck = () => {
    if (!validateCheck(currentCheck)) return;
    const newCheck: initialChecks = {
      _id: uuidv4(),
      checkType: currentCheck.checkType,
      frequency: currentCheck.frequency,
      nextScheduled: currentCheck.nextScheduled,
      createdAt: new Date().toISOString(),
    };
    setAssets((prev) => ({
      ...prev,
      checksAssets: [...prev.checksAssets, newCheck],
    }));
    setCurrentCheck({
      _id: "",
      checkType: "",
      frequency: "",
      nextScheduled: "",
      createdAt: "",
    });
  };

  const handleRemoveCheck = (checkId: string) => {
    setAssets((prev) => ({
      ...prev,
      checksAssets: prev.checksAssets.filter((c) => c._id !== checkId),
    }));
  };

  const handelMaintenanceHandler = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setMaintenanceState((prev) => ({ ...prev, [name]: value }));
    setMaintenanceHandled(true);
  };

  const handleSelectChange = (e: any) => {
    const selectedValue = e.target.value;
    if (selectedValue) {
      const sel = equipmentData.find((eq: any) => eq._id === selectedValue);
      setAssets((prev) => ({
        ...prev,
        equipmentInventory: selectedValue,
        equipmentInventoryName: sel ? sel.equipmentName : "",
      }));
    } else {
      setAssets((prev) => ({
        ...prev,
        equipmentInventory: "",
        equipmentInventoryName: "",
      }));
    }
  };

  const handleSubcategoryChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedValue = e.target.value;
    setSelectEquipment(selectedValue);
    setAssets((prev) => ({
      ...prev,
      equipmentSubCategory: selectedValue || "",
    }));
  };

  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);

  const filteredPremises = useMemo(() => {
    if (!selectedRegion) return allPremises;
    return allPremises.filter((p: any) => p.region === selectedRegion);
  }, [selectedRegion, allPremises]);

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;
    try {
      const file = e.target.files[0];
      const url = await uploadFileToS3(file);
      setFile(file);
      setAssets({ ...assets, image: url ? url : "" });
    } catch (err) {
      setError("Failed to upload image");
      console.error("Image upload error:", err);
    }
  };

  const uploadtos3 = async (file: File | null) => {
    if (!file) return;
    setIsLoading(true);
    try {
      const uploadedFileUrl = await uploadFileToS3(file);
      assets.image = uploadedFileUrl;
    } catch (err: any) {
      console.error(err?.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssetsUpdate = (a: any) => updateAssetsHandler(a._id, a);

  const handleDocumentUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;
    try {
      const url = await uploadFileToS3(e.target.files[0]);
      setAssets({ ...assets, uploadReferenceDocument: url });
    } catch (err) {
      setError("Failed to upload document");
      console.error("Document upload error:", err);
    }
  };

  const handleSubmit = (a: AssetsState) => createAssetsHandler(a);

  useEffect(() => {
    if (currentAssets && (isView || isEdit)) {
      const d = currentAssets;
      setAssets((prev) => ({
        ...prev,
        ...d,
        image: d.image || "",
        files: Array.isArray(d.files) ? d.files : [],
        equipmentInventoryId: currentAssets.equipmentInventory || "",
        equipmentInventoryName: d.equipmentInventory || "",
        equipmentSubCategory: d.equipmentSubCategory || "",
        premisesName: d.premisesName || "",
        premisesId: d.premisesId || "",
        equipmentManufactureDate: d.equipmentManufactureDate || "",
        equipmentExpireDate: d.equipmentExpireDate || "",
        checksAssets: Array.isArray(d.checksAssets) ? d.checksAssets : [],
        region: d.region || "",
        name: d.name || "",
        vendorName: d.vendorName || "",
        mobileNumber: d.mobileNumber || "",
        vendorEmail: d.vendorEmail || "",
        inventory: d.inventory || "",
        unitNumber: d.unitNumber || "",
        manufacture: d.manufacture || "",
        type: d.type || "",
        installedOn: d.installedOn || "",
        totalNumber: d.totalNumber || "",
        uploadReferenceDocument: d.uploadReferenceDocument || "",
        floor: d.floor || "",
        areaName: d.areaName || "",
      }));
      setSelectedPremise(d?.premisesId || d?.premisesName);
      setSelectedRegion(d?.region || "");
      setSelectedEquipmentManufacture(d.equipmentManufactureDate || "");
      setSelectedEquipmentExpire(d.equipmentExpireDate || "");
    }
  }, [currentAssets, mode]);

  useEffect(() => {
    if (isCreate) {
      setAssets({
        premisesName: "",
        premisesId: "",
        equipmentInventory: "",
        equipmentInventoryName: "",
        equipmentSubCategory: "",
        region: "",
        name: "",
        image: "",
        vendorName: "",
        mobileNumber: "",
        vendorEmail: "",
        inventory: "",
        unitNumber: "",
        manufacture: "",
        type: "",
        installedOn: "",
        totalNumber: "",
        uploadReferenceDocument: "",
        floor: "",
        areaName: "",
        checksAssets: [],
        files: [],
        equipmentManufactureDate: "",
        equipmentExpireDate: "",
      });
      setSelectedPremise("");
      setSelectedRegion("");
      setSelectedEquipmentManufacture("");
      setSelectedEquipmentExpire("");
    }
  }, [mode]);

  useEffect(() => {
    if (currentMaintenance) {
      setMaintenanceState((prev) => ({ ...prev, ...currentMaintenance }));
    }
  }, [currentMaintenance]);

  const singlePremiseObj: any = useMemo(() => {
    if (!selectedPremise) return null;
    return filteredPremises.find((p: any) => p._id === selectedPremise) || null;
  }, [selectedPremise, filteredPremises]);

  const isMaintenance = roleData?.roleName === "maintenance";
  const showForm =
    assets?.equipmentInventoryName && assets?.equipmentSubCategory;

  const pageTitle = isMaintenance
    ? "Maintenance"
    : isView
    ? "View Assets"
    : isEdit
    ? "Update Assets"
    : "Add Assets";

  return (
    <div className="flex w-full h-full flex-col bg-[#f8f8fa] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 shrink-0">
        <h1 className="font-semibold text-[#1b428a]">{pageTitle}</h1>
      </div>

      {/* Equipment Selector */}
      {!isMaintenance && (
        <div className="flex px-3 shrink-0">
          <div className="flex w-full h-[50px] px-3 bg-[#e4e8ee] items-center gap-4">
            <h2 className="font-medium text-sm whitespace-nowrap">
              Equipment Inventory :
            </h2>
            <div className="flex items-center gap-3 bg-[#f8f8fa] border-2 border-[#a5b7cf] rounded-lg px-2 py-1.5 w-[280px] relative">
              <select
                value={assets?.equipmentInventory || equipmentInventory || ""}
                onChange={handleSelectChange}
                disabled={isView}
                className="text-sm text-[#5a687c] font-medium outline-none flex-1 appearance-none bg-transparent"
              >
                <option value="">Select Equipment</option>
                {equipmentData.map((v: any, i: number) => (
                  <option key={i} value={v._id}>
                    {v.equipmentName}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 pointer-events-none" />
            </div>
            <div className="flex items-center gap-3 bg-[#f8f8fa] border-2 border-[#a5b7cf] rounded-lg px-2 py-1.5 w-[280px] relative">
              <select
                name="subCategory"
                value={selectEquipment || assets.equipmentSubCategory || ""}
                onChange={handleSubcategoryChange}
                disabled={isView}
                className="text-sm text-[#5a687c] font-medium outline-none flex-1 appearance-none bg-transparent"
              >
                <option value="">Select Sub-Category</option>
                {assetSubcategoryData.map((v: any, i: number) => (
                  <option key={i} value={v._id}>
                    {v.subcategoryName}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 pointer-events-none" />
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      {showForm ? (
        isLoading ? (
          <DotLoader />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto bg-white mx-3 mt-1">
              <div className="px-5 py-4 space-y-6">
                {!isMaintenance ? (
                  <>
                    {/* ── Basic Information ── */}
                    <div>
                      <h3 className="text-sm font-semibold text-[#1b428a] mb-3 pb-1 border-b border-gray-200">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-4 gap-x-4 gap-y-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Region</label>
                          <div className="relative">
                            <select
                              name="region"
                              value={assets.region}
                              onChange={(e) => {
                                handleAssetsInput(e);
                                setSelectedRegion(e.target.value);
                              }}
                              disabled={isView}
                              className={fieldClass(isView) + " appearance-none pr-7"}
                            >
                              <option value="">Select Region</option>
                              {regions.map((r: any) => (
                                <option key={r._id} value={r._id}>{r.regionName}</option>
                              ))}
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                          </div>
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Premises</label>
                          <div className="relative">
                            <select
                              name="premisesId"
                              value={assets.premisesId}
                              onChange={(e) => {
                                const id = e.target.value;
                                const prem = filteredPremises.find(
                                  (p: any) => p._id === id
                                );
                                setAssets((prev) => ({
                                  ...prev,
                                  premisesId: id,
                                  premisesName: prem ? prem.premisesName : "",
                                }));
                                setSelectedPremise(id);
                              }}
                              disabled={isView}
                              className={fieldClass(isView) + " appearance-none pr-7"}
                            >
                              <option value="">Select Premises</option>
                              {filteredPremises.map((p: any, i: number) => (
                                <option key={i} value={p._id}>{p.premisesName}</option>
                              ))}
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                          </div>
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Name</label>
                          <input
                            type="text"
                            name="name"
                            value={assets.name}
                            onChange={handleAssetsInput}
                            disabled={isView}
                            placeholder="Asset name"
                            className={fieldClass(isView)}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Vendor Name</label>
                          <input
                            type="text"
                            name="vendorName"
                            value={assets.vendorName}
                            onChange={handleAssetsInput}
                            disabled={isView}
                            placeholder="Vendor name"
                            className={fieldClass(isView)}
                          />
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Mobile Number</label>
                          <input
                            type="text"
                            name="mobileNumber"
                            value={assets.mobileNumber}
                            onChange={handleAssetsInput}
                            disabled={isView}
                            placeholder="10-digit number"
                            maxLength={10}
                            className={fieldClass(isView)}
                          />
                          {error && assets.mobileNumber && (
                            <span className="text-red-500 text-xs">{error}</span>
                          )}
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Vendor Email</label>
                          <input
                            type="email"
                            name="vendorEmail"
                            value={assets.vendorEmail}
                            onChange={handleAssetsInput}
                            disabled={isView}
                            placeholder="vendor@email.com"
                            className={fieldClass(isView)}
                          />
                        </div>

                        {/* Image upload inline */}
                        <div className="flex flex-col gap-1 col-span-2">
                          <label className="text-xs font-semibold text-gray-500">Asset Image</label>
                          <div className="flex items-center gap-3">
                            <label
                              htmlFor="fileInput"
                              className={`flex items-center justify-center w-16 h-16 rounded-lg border-2 border-dashed border-[#a5b7cf] ${
                                isView ? "cursor-default" : "cursor-pointer hover:border-[#00A8A5]"
                              } transition-all overflow-hidden shrink-0`}
                            >
                              {imageUrl ? (
                                <img src={imageUrl} alt="Asset" className="w-full h-full object-cover" />
                              ) : (
                                <ImageIcon className="w-5 h-5 text-gray-400" />
                              )}
                            </label>
                            <input
                              type="file"
                              className="hidden"
                              id="fileInput"
                              accept="image/*"
                              onChange={handleImageUpload}
                              disabled={isView}
                            />
                            {!isView && (file || imageUrl) && (
                              <div className="flex gap-2">
                                {file && (
                                  <button
                                    type="button"
                                    onClick={() => uploadtos3(file)}
                                    className="px-3 py-1.5 text-xs font-medium bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87]"
                                  >
                                    Upload
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => { setImageSrc(null); setFile(null); }}
                                  className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Inventory Details ── */}
                    <div>
                      <h3 className="text-sm font-semibold text-[#1b428a] mb-3 pb-1 border-b border-gray-200">
                        Inventory Details
                      </h3>
                      <div className="grid grid-cols-4 gap-x-4 gap-y-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Inventory</label>
                          <div className="relative">
                            <select
                              name="inventory"
                              value={assets.inventory}
                              onChange={handleAssetsInput}
                              disabled={isView}
                              className={fieldClass(isView) + " appearance-none pr-7"}
                            >
                              <option value="">Select Inventory</option>
                              {INVENTORY_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                          </div>
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Unit Number</label>
                          <input type="text" name="unitNumber" value={assets.unitNumber} onChange={handleAssetsInput} disabled={isView} placeholder="Unit Number" className={fieldClass(isView)} />
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Manufacturer</label>
                          <input type="text" name="manufacture" value={assets.manufacture} onChange={handleAssetsInput} disabled={isView} placeholder="Manufacturer" className={fieldClass(isView)} />
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Type</label>
                          <input type="text" name="type" value={assets.type} onChange={handleAssetsInput} disabled={isView} placeholder="Type" className={fieldClass(isView)} />
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Installed On</label>
                          <input type="date" name="installedOn" value={assets.installedOn} onChange={handleAssetsInput} disabled={isView} className={fieldClass(isView)} />
                        </div>

                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Total Number</label>
                          <input type="text" name="totalNumber" value={assets.totalNumber} onChange={handleAssetsInput} disabled={isView} placeholder="Total Number" className={fieldClass(isView)} />
                        </div>

                        <div className="flex flex-col gap-1 col-span-2">
                          <label className="text-xs font-semibold text-gray-500">Upload Reference Document</label>
                          <input type="file" onChange={handleDocumentUpload} disabled={isView} className={fieldClass(isView)} />
                        </div>
                      </div>

                      {/* Location & Dates */}
                      <div className="grid grid-cols-4 gap-x-4 gap-y-3 mt-4 p-3 bg-[#f0f8ff] rounded-lg">
                        {singlePremiseObj && (
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-gray-600">Floor</label>
                            <div className="relative">
                              <select
                                name="floor"
                                value={assets.floor || ""}
                                onChange={handleAssetsInput}
                                disabled={isView}
                                className={fieldClass(isView) + " appearance-none pr-7"}
                              >
                                <option value="">Select Floor</option>
                                {singlePremiseObj.basement && (
                                  <option value="Basement">Basement</option>
                                )}
                                {Array.from(
                                  { length: singlePremiseObj?.floor ?? 0 },
                                  (_, i) => (
                                    <option key={i + 1} value={`Floor ${i + 1}`}>
                                      Floor {i + 1}
                                    </option>
                                  )
                                )}
                              </select>
                              <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </div>
                        )}
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-600">Area Name</label>
                          <input type="text" name="areaName" value={assets.areaName} onChange={handleAssetsInput} disabled={isView} className={fieldClass(isView)} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-600">Manufacture Date</label>
                          <input type="date" name="equipmentManufactureDate" value={assets.equipmentManufactureDate} onChange={handleAssetsInput} disabled={isView} className={fieldClass(isView)} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-600">Expire Date</label>
                          <input type="date" name="equipmentExpireDate" value={assets.equipmentExpireDate} onChange={handleAssetsInput} disabled={isView} className={fieldClass(isView)} />
                        </div>
                      </div>
                    </div>

                    {/* ── Asset Details (Dynamic) ── */}
                    <div>
                      <h3 className="text-sm font-semibold text-[#1b428a] mb-3 pb-1 border-b border-gray-200">
                        Asset Details
                      </h3>
                      {(() => {
                        const sel = equipmentData.find(
                          (e: any) =>
                            e._id ===
                            (currentAssets?.equipmentInventory ||
                              assets.equipmentInventory)
                        );
                        return (
                          <DynamicAssetFields
                            equipmentInventoryName={sel?.equipmentName}
                            values={assets}
                            onChange={(name, value) =>
                              setAssets((s: any) => ({ ...s, [name]: value }))
                            }
                            disabled={isView}
                            title=""
                          />
                        );
                      })()}
                    </div>

                    {/* ── Scheduled Checks (commented out) ── */}
                    {/*
                    <div>
                      <h3 className="text-sm font-semibold text-[#1b428a] mb-3 pb-1 border-b border-gray-200">
                        Scheduled Checks
                      </h3>
                      <div className="flex gap-4">
                        {!isView && (
                          <div className="w-64 shrink-0 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-gray-500">Check Type</label>
                              <input type="text" name="checkType" value={currentCheck.checkType} onChange={handleCheckInput} placeholder="e.g. Fire inspection" className={fieldClass(false)} />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-gray-500">Frequency</label>
                              <div className="relative">
                                <select name="frequency" value={currentCheck.frequency} onChange={handleCheckInput} className={fieldClass(false) + " appearance-none pr-7"}>
                                  <option value="">Select Frequency</option>
                                  {FREQUENCY_OPTIONS.map((f) => (
                                    <option key={f} value={f}>{f}</option>
                                  ))}
                                </select>
                                <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-gray-500">Next Scheduled</label>
                              <input type="date" value={currentCheck.nextScheduled} onChange={(e) => setCurrentCheck({ ...currentCheck, nextScheduled: e.target.value })} min={new Date().toISOString().split("T")[0]} className={fieldClass(false)} />
                            </div>
                            <button type="button" onClick={handleAddCheck} className="w-full px-3 py-2 text-xs font-medium text-white bg-[#0fa9a6] rounded-lg hover:bg-[#008A87]">
                              ADD
                            </button>
                          </div>
                        )}
                        <div className="flex-1">
                          {assets.checksAssets.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-8">No checks added yet</p>
                          ) : (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-[#E9F5FF] text-left">
                                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Check Type</th>
                                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Frequency</th>
                                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Next Scheduled</th>
                                    {!isView && <th className="px-3 py-2 text-xs font-semibold text-gray-600 w-14">Action</th>}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {assets.checksAssets.map((check) => (
                                    <tr key={check._id}>
                                      <td className="px-3 py-2 text-gray-700">{check.checkType}</td>
                                      <td className="px-3 py-2 text-gray-600">{check.frequency}</td>
                                      <td className="px-3 py-2 text-gray-600">{check.nextScheduled}</td>
                                      {!isView && (
                                        <td className="px-3 py-2">
                                          <button onClick={() => handleRemoveCheck(check._id || "")} className="text-red-500 hover:text-red-700">
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    */}
                  </>
                ) : (
                  <>
                    {/* ── Maintenance Form ── */}
                    <div>
                      <h3 className="text-sm font-semibold text-[#1b428a] mb-3 pb-1 border-b border-gray-200">
                        Maintenance
                      </h3>
                      <div className="grid grid-cols-4 gap-x-4 gap-y-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Check Internal/External</label>
                          <div className="relative">
                            <select
                              name="internalExternal"
                              value={maintenanceState.internalExternal}
                              onChange={handelMaintenanceHandler}
                              disabled={isView}
                              className={fieldClass(isView) + " appearance-none pr-7"}
                            >
                              <option value="">Select Category</option>
                              <option value="Internal">Internal</option>
                              <option value="External">External</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">What is Checked</label>
                          <textarea name="Checked" value={maintenanceState.Checked} onChange={handelMaintenanceHandler} disabled={isView} rows={1} className={fieldClass(isView) + " resize-none"} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Last Date of Check</label>
                          <input type="date" name="lastDateOfCheck" max={maxDate} value={maintenanceState.lastDateOfCheck} onChange={handelMaintenanceHandler} disabled={isView} className={fieldClass(isView)} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Check Completed By</label>
                          <input type="text" name="checkCompletedBy" value={maintenanceState.checkCompletedBy} onChange={handelMaintenanceHandler} disabled={isView} className={fieldClass(isView)} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Observation</label>
                          <textarea name="observation" value={maintenanceState.observation} onChange={handelMaintenanceHandler} disabled={isView} rows={1} className={fieldClass(isView) + " resize-none"} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Action Taken</label>
                          <textarea name="actionTaken" value={maintenanceState.actionTaken} onChange={handelMaintenanceHandler} disabled={isView} rows={1} className={fieldClass(isView) + " resize-none"} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Recheck Date</label>
                          <input type="date" name="recheckDate" min={minDate} value={maintenanceState.recheckDate} onChange={handelMaintenanceHandler} disabled={isView} className={fieldClass(isView)} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-500">Recheck By</label>
                          <input type="text" name="recheckBy" value={maintenanceState.recheckBy} onChange={handelMaintenanceHandler} disabled={isView} className={fieldClass(isView)} />
                        </div>
                      </div>

                      {/* File Upload */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-semibold text-gray-500">Upload Files</label>
                          {!isView && (
                            <span className="text-xs text-gray-400">
                              DOC/DOCX, PDF, TXT, JPG, PNG (Max: 5MB)
                            </span>
                          )}
                        </div>
                        <div
                          className={`border-2 ${isView ? "border-gray-200" : "border-dashed border-[#a5b7cf]"} rounded-lg p-4 text-center`}
                          {...(!isView ? getRootProps() : {})}
                        >
                          {!isView && <input {...getInputProps()} />}
                          <p className="text-gray-500 text-sm">
                            {isView ? "Attached Files" : "Drag & Drop Files Here or Click"}
                          </p>
                          {Array.isArray(assets.files) && assets.files.length > 0 && (
                            <div className="flex gap-3 mt-3 flex-wrap justify-center">
                              {assets.files.map((f, i) => (
                                <div key={i} className="flex flex-col items-center bg-gray-100 p-2 rounded-md w-20 relative group">
                                  <FileText size={24} className="text-blue-500" />
                                  <p className="text-xs text-gray-600 truncate w-full text-center">{f.name}</p>
                                  {!isView && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleRemoveFile(i); }}
                                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100"
                                    >
                                      <X className="w-2.5 h-2.5" />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleViewFile(f); }}
                                    className="absolute -top-1 -left-1 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100"
                                    title="View file"
                                  >
                                    <span className="text-[8px]">&#128065;</span>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {(!Array.isArray(assets.files) || assets.files.length === 0) && (
                            <p className="text-gray-400 text-xs mt-1">
                              {isView ? "No files attached" : "No files selected"}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Maintenance Records */}
                    {allMaintenance && allMaintenance.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-[#1b428a] mb-3 pb-1 border-b border-gray-200">
                          Maintenance Records
                        </h3>
                        <div className="border border-gray-200 rounded-lg overflow-auto max-h-[200px]">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0">
                              <tr className="bg-[#E9F5FF] text-left">
                                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Type</th>
                                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Checked</th>
                                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Last Check</th>
                                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Completed By</th>
                                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Observation</th>
                                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Action</th>
                                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Recheck</th>
                                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Recheck By</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {allMaintenance.map((item: any, i: number) => (
                                <tr key={i} className="hover:bg-gray-50/50">
                                  <td className="px-3 py-2 text-gray-700">{item.internalExternal}</td>
                                  <td className="px-3 py-2 text-gray-600">{item.Checked}</td>
                                  <td className="px-3 py-2 text-gray-600">{item.lastDateOfCheck}</td>
                                  <td className="px-3 py-2 text-gray-600">{item.checkCompletedBy}</td>
                                  <td className="px-3 py-2 text-gray-600">{item.observation}</td>
                                  <td className="px-3 py-2 text-gray-600">{item.actionTaken}</td>
                                  <td className="px-3 py-2 text-gray-600">{item.recheckDate}</td>
                                  <td className="px-3 py-2 text-gray-600">{item.recheckBy}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 w-full h-16 flex justify-end items-center px-5 bg-[#61748e]">
              {isView && (
                <button
                  onClick={() => { setIsCreating(false); clearData(); }}
                  className="px-4 py-2 rounded-2xl bg-[#869bb7]"
                >
                  <span className="text-white text-lg font-medium">CANCEL</span>
                </button>
              )}
              {isEdit && isMaintenance && (
                <button
                  onClick={() => {
                    generateMaintenanceExcel(allMaintenance || []);
                    clearData();
                    setSelectedIds([]);
                  }}
                  className="px-4 py-2 mr-4 rounded-2xl bg-[#0fa9a6]"
                >
                  <span className="text-white text-lg font-medium">Download</span>
                </button>
              )}
              {isEdit && (
                <button
                  onClick={() => {
                    handleAssetsUpdate(assets);
                    if (maintenanceHandled) createMaintenanceHandler(maintenanceState);
                    setMaintenanceHandled(false);
                    setIsCreating(false);
                    clearData();
                    setSelectedIds([]);
                  }}
                  className="px-4 py-2 rounded-2xl bg-[#0fa9a6]"
                >
                  <span className="text-white text-lg font-medium">UPDATE</span>
                </button>
              )}
              {isCreate && (
                <button
                  onClick={() => {
                    handleSubmit(assets);
                    setIsCreating(false);
                    if (maintenanceHandled) createMaintenanceHandler(maintenanceState);
                    setMaintenanceHandled(false);
                    clearData();
                  }}
                  className="px-4 py-2 rounded-2xl bg-[#0fa9a6]"
                >
                  <span className="text-white text-lg font-medium">SUBMIT</span>
                </button>
              )}
            </div>
          </>
        )
      ) : (
        <div className="flex flex-col-reverse w-full flex-1 bg-[#f8f8fa] justify-center items-center">
          <h1>Select Equipment Inventory to Add Assets</h1>
          <img src={images.selectAssetsImg} width={165} alt="select-asset" />
        </div>
      )}
    </div>
  );
};

export default AddAssets;
